/**
 * Provider Call Context
 *
 * Centralized state management for provider video calls.
 *
 * ## Usage
 *
 * 1. Wrap your component tree in ProviderCallContextController:
 * ```typescript
 * <DailyCallProvider>
 *   <ProviderCallContextController
 *     roomId={roomId}
 *     appointment={appointment}
 *     isProvider={true}
 *   >
 *     <YourComponent />
 *   </ProviderCallContextController>
 * </DailyCallProvider>
 * ```
 *
 * 2. Access context in child components:
 * ```typescript
 * const { lifecycle, duration, ui } = useProviderCallContext();
 * ```
 *
 * ## State Organization
 *
 * State is organized into three logical groups:
 * - **lifecycle**: Appointment creation and participant tracking
 * - **duration**: Time calculation and manual editing
 * - **ui**: Modal state and user interactions
 *
 * @module ProviderCallContext
 */

// Public API - Components and hooks for application code
export { ProviderCallContextController } from './ProviderCallContextController';
export { useProviderCallContext } from './useProviderCallContext';

// Types - Public interfaces for TypeScript consumers
export type {
  AppointmentLifecycleState,
  DurationState,
  ProviderCallContextValue,
  UIState,
} from './ProviderCallContext';

// Internal - Exported for testing only, do not use in application code
// Use useProviderCallContext() instead
export { useProviderCallState } from './useProviderCallState';
export { useTranscriptionPermissions } from './useTranscriptionPermissions';
